import { useMemo } from "react";
import { Button } from "@storybook";

import { Card, FormContainer, LabelDescription } from "components";
import { useSignUp } from "views/signup/stores";

interface IAccountDetails {
  handleBack: () => void;
  handleSubmit: () => void;
}

/**
 * AccountDetails Component
 * Displays the account details form and action buttons for registration.
 * @param {Object} props - Component props
 * @param {Function} props.handleBack - Function to handle navigation back
 * @param {Function} props.handleSubmit - Function to handle form submission
 */
export const AccountDetails = ({ handleBack }: IAccountDetails) => {
  // Destructuring state and functions from the useSignUp hook
  const {
    form,
    handleChange,
    handleChangeCountry,
    handleSubmitRegistration,
    loadingRegistration,
    loadingRegisterationTracking,
    isCardLoading
  } = useSignUp();

  /**
   * Determines if the continue button should be disabled based on form errors or empty values
   */
  const isButtonDisabled = useMemo(() => {
    return !!form.find((el) => el.isError || !el.value);
  }, [form]);

  const formInputs = useMemo(() => {
    return form.map((el: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { disabled, ...rest } = el;
      return rest;
    });
  }, [form]);

  return (
    <Card
      className="flex direction-col gap-16 sm-border-0"
      isLoading={loadingRegistration || isCardLoading }
    >
      {/* Main container for the card  */}
      <>
        {/* Title and Description of the section */}
        <LabelDescription
          label="Account Details"
          description="Please fill in all the details required for registering."
        />
        <div className="flex wrap w-100 gap-16">
          {" "}
          {/* Container for form elements */}
          <FormContainer
            form={formInputs}
            handleChange={handleChange} // Handler for form input changes
            handleChangeCountry={handleChangeCountry}
          />
        </div>
        <div className="flex direction-col gap-8">
          {" "}
          {/* Container for action buttons */}
          <Button
            label={loadingRegisterationTracking.label || "Continue"} //Label for the continue button
            type="button__filled button__filled--primary button__block" //Button styling
            handleClick={handleSubmitRegistration} //Click handler for form submission
            disabled={
              isButtonDisabled ||
              loadingRegistration ||
              loadingRegisterationTracking.loading
            } //Disable button if form has errors or is loading
            loader={loadingRegisterationTracking.loading || loadingRegistration} //Show loading spinner if form is submitting
          />
          <Button
            label="Back" //Label for the back button
            type="button__filled button__block button__filled--secondary-hover" // Button styling
            handleClick={handleBack} // Click handler for navigating back
          />
        </div>
      </>
    </Card>
  );
};
